import React, { useState, useEffect, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTelegram } from '../contexts/TelegramContext';
import { useTheme } from '../contexts/ThemeContext';
import { getAppTitle } from '../utils/config';
import {
  HomeIcon,
  CreditCardIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  BookOpenIcon,
  TicketIcon,
  ClipboardDocumentListIcon,
  GiftIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

interface LayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: '仪表板', href: '/dashboard', icon: HomeIcon },
  { name: '订阅计划', href: '/plans', icon: CreditCardIcon },
  { name: '订单记录', href: '/orders', icon: ClipboardDocumentListIcon },
  { name: '节点状态', href: '/nodes', icon: Bars3Icon },
  { name: '使用教程', href: '/knowledge', icon: BookOpenIcon },
  { name: '我的工单', href: '/ticket', icon: TicketIcon },
  { name: '邀请管理', href: '/invite', icon: GiftIcon },
  { name: '个人资料', href: '/profile', icon: UserIcon },
];

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, loading, logout } = useAuth();
  const { isInTelegram, hapticFeedback } = useTelegram();
  const { toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    if (isInTelegram) {
      hapticFeedback('impact');
    }
    logout();
    navigate('/login');
  };

  const handleNavigation = (href: string) => {
    if (isInTelegram) {
      hapticFeedback('selection');
    }
    navigate(href);
    setSidebarOpen(false);
  };

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 z-50 lg:hidden transition-opacity duration-300',
        sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" onClick={() => setSidebarOpen(false)} />
        <div className={clsx(
          'relative flex w-full max-w-xs flex-1 flex-col bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}>
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white bg-gray-600 bg-opacity-75 hover:bg-opacity-100 transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent navigation={navigation} location={location} onItemClick={handleNavigation} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <SidebarContent navigation={navigation} location={location} onItemClick={handleNavigation} />
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              className="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                欢迎回来，<span className="font-medium"></span>
              </div>
              <button
                onClick={toggleTheme}
                className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title="切换主题"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="text-sm">退出</span>
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

interface SidebarContentProps {
  navigation: typeof navigation;
  location: ReturnType<typeof useLocation>;
  onItemClick?: (href: string) => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ navigation, location, onItemClick }) => {
  
  return (
    <div className="flex flex-1 flex-col overflow-y-auto border-r bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors">
      <div className="flex flex-1 flex-col pt-5 pb-4">
        <div className="flex flex-shrink-0 items-center px-4 mb-2">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white transition-colors">{getAppTitle()}</h1>
        </div>
        <nav className="mt-6 flex-1 space-y-1 px-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <button
                key={item.name}
                onClick={() => onItemClick?.(item.href)}
                className={clsx(
                    'group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 active:scale-95 w-full text-left',
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300 border-r-4 border-primary-500 dark:border-primary-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white hover:shadow-sm'
                  )}
              >
                <item.icon
                  className={clsx(
                    'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                    isActive 
                      ? 'text-primary-500 dark:text-primary-400'
                      : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-400 dark:group-hover:text-gray-300'
                  )}
                />
                <span className="truncate">{item.name}</span>
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 mt-auto transition-colors">
          <div className="text-xs text-center text-gray-500 dark:text-gray-400">
            © 2025 {getAppTitle()}
          </div>
        </div>
      </div>
    </div>
  );
};